import type { RowData, Table as TanstackTable } from '@tanstack/react-table';
import { Box } from 'hephaestus/web';
import type { ReactElement } from 'react';
import { Pagination, type PaginationVariant } from '../pagination/pagination';
import { ROWS_PER_PAGE } from './dataTable.constants';

export type DataTableTanstackPaginationOptions<_TData extends RowData> = {
  mode: 'tanstack';
  showPerPageSelect?: boolean;
  pageSizes?: number[];
  /**
   * `tableFooter`: full-width bar with first/last buttons (e.g. recon flags).
   * Omit for the legacy DataTable footer layout (`px` / `py` wrapper).
   */
  ovVariant?: PaginationVariant;
};

export type DataTableControlledPaginationOptions = {
  mode: 'controlled';
  pageCount: number;
  /** 1-based page index (matches design-system {@link Pagination}). */
  page: number;
  onPageChange: (page: number) => void;
  totalRowCount?: number;
  perPage?: number;
  onPerPageChange?: (perPage: number) => void;
  showPerPageSelect?: boolean;
  pageSizes?: number[];
  /** i18n namespace for the range summary (default `shared`). */
  summaryNamespace?: string;
  /** i18n key for the range summary (default `dataTablePaginationSummary`). */
  summaryKey?: string;
  /** @see {@link DataTableTanstackPaginationOptions.ovVariant} */
  ovVariant?: PaginationVariant;
};

export type DataTableTablePaginationOptions<TData extends RowData> =
  | DataTableTanstackPaginationOptions<TData>
  | DataTableControlledPaginationOptions;

type DataTableTablePaginationFooterProps<TData extends RowData> = {
  table: TanstackTable<TData>;
  options: DataTableTablePaginationOptions<TData>;
  /** When true (e.g. initial fetch), keep the footer mounted to avoid layout shift once rows arrive. */
  loading?: boolean;
};

const wrapDataTablePaginationFooter = (
  pagination: ReactElement,
  ovVariant?: PaginationVariant,
): ReactElement => {
  if (ovVariant === 'tableFooter') {
    return pagination;
  }
  return (
    <Box
      sx={{
        px: 2,
        py: 1,
      }}
    >
      {pagination}
    </Box>
  );
};

/**
 * Footer row for {@link DataTableView}: design-system {@link Pagination} wired to TanStack
 * client pagination or to explicit page handlers (server / custom).
 */
export function DataTableTablePaginationFooter<TData extends RowData>({
  table,
  options,
  loading = false,
}: DataTableTablePaginationFooterProps<TData>): JSX.Element | null {
  if (options.mode === 'controlled') {
    const {
      pageCount,
      page,
      onPageChange,
      totalRowCount,
      perPage,
      onPerPageChange,
      showPerPageSelect,
      pageSizes,
      summaryNamespace,
      summaryKey,
      ovVariant,
    } = options;
    const showFooter =
      loading ||
      pageCount > 1 ||
      Boolean(showPerPageSelect && onPerPageChange && perPage !== undefined);
    if (!showFooter) {
      return null;
    }
    const resolvedCount = Math.max(pageCount, 1);
    return wrapDataTablePaginationFooter(
      <Pagination
        ovVariant={ovVariant}
        count={resolvedCount}
        page={Math.min(page, resolvedCount)}
        onChange={(_event, value: number) => {
          onPageChange(value);
        }}
        total={totalRowCount}
        perPage={perPage}
        onChangePerPage={onPerPageChange}
        showPerPage={Boolean(showPerPageSelect && onPerPageChange && perPage)}
        pageSizes={pageSizes ?? [...ROWS_PER_PAGE]}
        summaryKey={summaryKey ?? 'dataTablePaginationSummary'}
        summaryNamespace={summaryNamespace}
        disabled={
          Boolean(loading) &&
          (totalRowCount === undefined || totalRowCount === 0)
        }
      />,
      ovVariant,
    );
  }

  const { showPerPageSelect, pageSizes, ovVariant } = options;
  const pageCount = table.getPageCount();
  const { pageIndex, pageSize } = table.getState().pagination;
  const showFooter = loading || pageCount > 1 || Boolean(showPerPageSelect);
  if (!showFooter) {
    return null;
  }

  const rowCount = table.getCoreRowModel().rows.length;
  const resolvedCount = Math.max(pageCount, 1);

  return wrapDataTablePaginationFooter(
    <Pagination
      ovVariant={ovVariant}
      count={resolvedCount}
      page={Math.min(pageIndex + 1, resolvedCount)}
      onChange={(_event, value: number) => {
        table.setPageIndex(value - 1);
      }}
      total={rowCount}
      perPage={pageSize}
      onChangePerPage={(next) => {
        table.setPageSize(Number(next));
        table.setPageIndex(0);
      }}
      showPerPage={Boolean(showPerPageSelect)}
      pageSizes={pageSizes ?? [...ROWS_PER_PAGE]}
      summaryKey='dataTablePaginationSummary'
      disabled={Boolean(loading && rowCount === 0)}
    />,
    ovVariant,
  );
}
